import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTemplateFromConstruction } from "../../hooks/use-template-from-construction.ts";
import {
	type TemplateFormValues,
	templateSchema,
} from "../../schemas/template-schema.ts";

const VIOLATION_FIELDS: Record<string, keyof TemplateFormValues> = {
	name: "name",
	description: "description",
};

export function SaveAsTemplateDialog({
	open,
	onOpenChange,
	constructionId,
	constructionName,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	constructionName?: string;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const fromConstruction = useTemplateFromConstruction();

	const {
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<TemplateFormValues>({
		resolver: zodResolver(templateSchema),
		defaultValues: { name: "", description: "" },
	});

	useEffect(() => {
		if (open) {
			reset({
				name: constructionName ?? "",
				description: "",
			});
			setFormError(null);
		}
	}, [open, constructionName, reset]);

	async function onSubmit(values: TemplateFormValues) {
		setFormError(null);
		try {
			await fromConstruction.mutateAsync({
				constructionId,
				name: values.name,
				description: values.description?.trim() || undefined,
			});
			toast.success("Template criado a partir das etapas do canteiro!");
			onOpenChange(false);
		} catch (err) {
			if (err instanceof APIError) {
				let matched = false;
				for (const [field, message] of Object.entries(err.violations)) {
					const formField = VIOLATION_FIELDS[field];
					if (formField) {
						setError(formField, { message });
						matched = true;
					}
				}
				if (!matched) setFormError(err.message);
				return;
			}
			setFormError(
				err instanceof Error
					? err.message
					: "Houve um erro ao criar o template.",
			);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Salvar como template</DialogTitle>
					<DialogDescription>
						Cria um template da conta com as etapas atuais deste canteiro. As
						etapas originais permanecem inalteradas.
					</DialogDescription>
				</DialogHeader>
				<form
					id="save-as-template-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					className="template-form"
				>
					<div className="form-group">
						<Label htmlFor="save-template-name">Nome</Label>
						<Input
							id="save-template-name"
							placeholder="Ex.: Obra residencial padrão"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="save-template-description">Descrição</Label>
						<Textarea
							id="save-template-description"
							placeholder="Detalhes do template (opcional)"
							aria-invalid={!!errors.description}
							{...register("description")}
						/>
						{errors.description && (
							<p className="form-error">{errors.description.message}</p>
						)}
					</div>

					{formError && (
						<p role="alert" className="template-form__error">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={fromConstruction.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						form="save-as-template-form"
						disabled={fromConstruction.isPending}
					>
						{fromConstruction.isPending && (
							<Loader2 className="template-spinner" />
						)}
						Salvar template
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
